import React, { useEffect, useReducer } from 'react'
import { styled } from '@mui/material/styles'
import * as XLSX from 'xlsx'
import './style.scss'
import {
    Button,
    Dialog,
    FormControl,
    FormGroup,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
} from '@mui/material'
import { GridSearchIcon } from '@mui/x-data-grid'
import {
    createReadingBook,
    getReadingBooks,
    updateReadingBook,
    bulkUploadReadingBooks,
    bulkDeleteReadingBooks,
} from '../../services/reading-books'
import { isMobileScreen, modifyProperty } from '../../utils'
import { ALERT, BOOK_CATEGORY } from '../../constants'
import {
    CheckCircleRounded,
    Edit,
    Visibility,
    CloudDownload,
    CloudUpload,
    Delete,
} from '@mui/icons-material'
import store from '../../store'
import { showAlert } from '../../actions'
import { CustomDataGrid } from '../../components/CustomTable/CustomDataGrid'

const PREFIX = 'ReadingBooksContainer'

const classes = {
    tableHeader: `${PREFIX}-tableHeader`,
    modal: `${PREFIX}-modal`,
    modalRoot: `${PREFIX}-modalRoot`,
    paper: `${PREFIX}-paper`,
}

const Root = styled('div')({
    [`& .${classes.tableHeader}`]: {
        padding: '20px',
        marginBottom: '20px',
    },
    [`& .${classes.modal}`]: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    [`& .${classes.modalRoot}`]: {
        position: 'absolute',
        flexGrow: 1,
        minWidth: 300,
        transform: 'translateZ(0)',
        '@media all and (-ms-high-contrast: none)': {
            display: 'none',
        },
    },
})

const styles = {
    paper: {
        width: 500,
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '40px',
        lineHeight: '1.44',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
}

const initialState = {
    data: [],
    form: {
        title: '',
        author: '',
        category: '',
        pages: '',
        duration_days: '',
        description: '',
        url: '',
    },
    modal: false,
    editMode: false,
    editId: null,
    uploadFile: null,
    selectedIds: [],
    filters: {
        pageSize: 10,
        page: 0,
        total_results: 0,
        total_pages: 0,
        search: '',
        category: '',
    },
}

function ReadingBooksContainer() {
    const [
        {
            data,
            modal,
            form,
            loading,
            filters,
            editMode,
            editId,
            uploadFile,
            selectedIds,
        },
        setState,
    ] = useReducer(
        (prev, data) => ({
            ...prev,
            ...data,
            filters: { ...prev.filters, ...data.filters },
            form: { ...prev.form, ...data.form },
        }),
        initialState
    )

    const isMobile = isMobileScreen()
    const fileInputRef = React.useRef(null)

    const fetchData = () => {
        const params = {
            page: filters.page + 1,
            per_page: filters.pageSize,
            search: filters.search,
            category: filters.category,
        }
        setState({ loading: true })
        getReadingBooks(params)
            .then((res) => {
                const { data, total_pages, total_results } = res?.data || {}
                setState({
                    data: data || [],
                    loading: false,
                    selectedIds: [],
                    filters: {
                        total_results: total_results || 0,
                        total_pages: total_pages || 0,
                    },
                })
            })
            .catch((error) => {
                setState({ loading: false })
                store.dispatch(
                    showAlert({
                        message: 'Failed to fetch reading books',
                        severity: ALERT.ERROR,
                        duration: 5000,
                    })
                )
            })
    }

    useEffect(() => {
        fetchData()
    }, [
        filters.search,
        filters.category,
        filters.page,
        filters.pageSize,
    ])

    const rowWidthConfig = {
        title: { default: { flex: 0.2 }, mobile: { width: 150 } },
        author: { default: { flex: 0.15 }, mobile: { width: 120 } },
        category: { default: { flex: 0.15 }, mobile: { width: 120 } },
        pages: { default: { flex: 0.1 }, mobile: { width: 80 } },
        duration_days: { default: { flex: 0.1 }, mobile: { width: 100 } },
        action: { default: { flex: 0.15 }, mobile: { width: 120 } },
    }

    const headCells = [
        {
            field: 'title',
            numeric: false,
            headerName: 'Title',
            sortable: false,
        },
        {
            field: 'author',
            numeric: false,
            headerName: 'Author',
            sortable: false,
        },
        {
            field: 'category',
            numeric: false,
            headerName: 'Category',
            sortable: false,
        },
        {
            field: 'pages',
            numeric: true,
            headerName: 'Pages',
            sortable: false,
        },
        {
            field: 'duration_days',
            numeric: true,
            headerName: 'Duration (Days)',
            sortable: false,
        },
        {
            field: 'action',
            headerName: 'Action',
            renderCell: (params) => {
                return (
                    <>
                        {params.row.url && (
                            <IconButton
                                onClick={() => {
                                    window.open(
                                        params.row.url,
                                        '_blank'
                                    )
                                }}
                                aria-label="View"
                                color="primary"
                                title="View"
                                size="large"
                            >
                                <Visibility />
                            </IconButton>
                        )}
                        <IconButton
                            onClick={() => handleEdit(params.row)}
                            aria-label="Edit"
                            color="primary"
                            title="Edit"
                            size="large"
                        >
                            <Edit />
                        </IconButton>
                    </>
                )
            },
        },
    ]

    const onFilterChange = (name, value) => {
        setState({
            filters: {
                [name]: value,
                page: 0,
            },
        })
    }

    const onPageSizeChange = ({ page, pageSize }) => {
        setState({
            filters: {
                page,
                pageSize,
            },
        })
    }

    const handleSelectionChange = (newSelection) => {
        setState({ selectedIds: newSelection })
    }

    const handleInput = (name, value) => {
        setState({
            form: {
                [name]: value,
            },
        })
    }

    const handleEdit = (row) => {
        setState({
            modal: true,
            editMode: true,
            editId: row.id,
            form: {
                title: row.title || '',
                author: row.author || '',
                category: row.categoryId || '',
                pages: row.pages || '',
                duration_days: row.duration_days || '',
                description: row.description || '',
                url: row.url || '',
            },
        })
    }

    const validateForm = () => {
        const { title, author, category, pages, duration_days } = form

        if (!title || !title.trim()) {
            store.dispatch(
                showAlert({
                    message: 'Title is required',
                    severity: ALERT.ERROR,
                    duration: 5000,
                })
            )
            return false
        }

        if (!author || !author.trim()) {
            store.dispatch(
                showAlert({
                    message: 'Author is required',
                    severity: ALERT.ERROR,
                    duration: 5000,
                })
            )
            return false
        }

        if (!category) {
            store.dispatch(
                showAlert({
                    message: 'Category is required',
                    severity: ALERT.ERROR,
                    duration: 5000,
                })
            )
            return false
        }

        if (!pages || pages <= 0) {
            store.dispatch(
                showAlert({
                    message: 'Pages must be a positive number',
                    severity: ALERT.ERROR,
                    duration: 5000,
                })
            )
            return false
        }

        if (!duration_days || duration_days <= 0) {
            store.dispatch(
                showAlert({
                    message: 'Duration must be a positive number',
                    severity: ALERT.ERROR,
                    duration: 5000,
                })
            )
            return false
        }

        return true
    }

    const handleSubmit = () => {
        if (!validateForm()) {
            return
        }

        const body = {
            title: form.title,
            author: form.author,
            category: form.category,
            pages: parseInt(form.pages),
            duration_days: parseInt(form.duration_days),
            description: form.description || '',
            url: form.url || '',
        }

        setState({ loading: true })

        const apiCall = editMode
            ? updateReadingBook(editId, body)
            : createReadingBook(body)

        apiCall
            .then(() => {
                store.dispatch(
                    showAlert({
                        message: `Book ${editMode ? 'updated' : 'created'} successfully`,
                        severity: ALERT.SUCCESS,
                        duration: 3000,
                    })
                )
                fetchData()
                setState({
                    modal: false,
                    loading: false,
                    editMode: false,
                    editId: null,
                    form: {
                        title: '',
                        author: '',
                        category: '',
                        pages: '',
                        duration_days: '',
                        description: '',
                        url: '',
                    },
                })
            })
            .catch((error) => {
                setState({ loading: false })
            })
    }

    const handleCloseModal = () => {
        setState({
            modal: false,
            editMode: false,
            editId: null,
            form: {
                title: '',
                author: '',
                category: '',
                pages: '',
                duration_days: '',
                description: '',
                url: '',
            },
        })
    }

    const downloadTemplate = () => {
        const headers = [
            'title',
            'author',
            'category',
            'pages',
            'duration_days',
            'description',
            'url',
        ]
        const csvContent =
            headers.join(',') +
            '\n' +
            'Sample Book,Sample Author,BOOK_CAT_1,300,15,Sample description,https://example.com'

        const blob = new Blob([csvContent], {
            type: 'text/csv;charset=utf-8;',
        })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', 'reading_books_template.csv')
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        store.dispatch(
            showAlert({
                message: 'Template downloaded successfully',
                severity: ALERT.SUCCESS,
                duration: 3000,
            })
        )
    }

    const handleFileChange = (event) => {
        const file = event.target.files[0]
        if (file) {
            const validTypes = [
                'text/csv',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            ]
            if (!validTypes.includes(file.type)) {
                store.dispatch(
                    showAlert({
                        message:
                            'Invalid file type. Please upload CSV or Excel file.',
                        severity: ALERT.ERROR,
                        duration: 5000,
                    })
                )
                return
            }
            setState({ uploadFile: file })
        }
    }

    const handleBulkUpload = () => {
        if (!uploadFile) {
            store.dispatch(
                showAlert({
                    message: 'Please select a file to upload',
                    severity: ALERT.ERROR,
                    duration: 5000,
                })
            )
            return
        }

        setState({ loading: true })

        const reader = new FileReader()
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result)
                const workbook = XLSX.read(data, { type: 'array' })
                const sheetName = workbook.SheetNames[0]
                const worksheet = workbook.Sheets[sheetName]
                const jsonData = XLSX.utils.sheet_to_json(worksheet)

                // Validate and format the data
                const books = jsonData.map((row) => ({
                    title: row.title || '',
                    author: row.author || '',
                    category: row.category || '',
                    pages: parseInt(row.pages) || 0,
                    duration_days: parseInt(row.duration_days) || 0,
                    description: row.description || '',
                    url: row.url || '',
                }))

                // Filter out invalid entries
                const validBooks = books.filter(
                    (book) =>
                        book.title &&
                        book.author &&
                        book.category &&
                        book.pages > 0 &&
                        book.duration_days > 0
                )

                if (validBooks.length === 0) {
                    store.dispatch(
                        showAlert({
                            message:
                                'No valid book entries found in the file',
                            severity: ALERT.ERROR,
                            duration: 5000,
                        })
                    )
                    setState({ loading: false })
                    return
                }

                bulkUploadReadingBooks(validBooks)
                    .then((res) => {
                        store.dispatch(
                            showAlert({
                                message:
                                    res?.message ||
                                    `${validBooks.length} book(s) uploaded successfully`,
                                severity: ALERT.SUCCESS,
                                duration: 3000,
                            })
                        )
                        setState({ uploadFile: null, loading: false })
                        if (fileInputRef.current) {
                            fileInputRef.current.value = ''
                        }
                        fetchData()
                    })
                    .catch((error) => {
                        setState({ loading: false })
                        store.dispatch(
                            showAlert({
                                message:
                                    error?.response?.data?.message ||
                                    'Failed to upload books',
                                severity: ALERT.ERROR,
                                duration: 5000,
                            })
                        )
                    })
            } catch (error) {
                setState({ loading: false })
                store.dispatch(
                    showAlert({
                        message: 'Failed to parse file. Please check the format.',
                        severity: ALERT.ERROR,
                        duration: 5000,
                    })
                )
            }
        }
        reader.onerror = () => {
            setState({ loading: false })
            store.dispatch(
                showAlert({
                    message: 'Failed to read file',
                    severity: ALERT.ERROR,
                    duration: 5000,
                })
            )
        }
        reader.readAsArrayBuffer(uploadFile)
    }

    const handleBulkDelete = () => {
        if (!selectedIds.length) {
            store.dispatch(
                showAlert({
                    message: 'Please select at least one record to delete',
                    severity: ALERT.ERROR,
                    duration: 5000,
                })
            )
            return
        }

        const proceed = window.confirm(
            `Are you sure you want to delete ${selectedIds.length} record(s)?`
        )

        if (!proceed) {
            return
        }

        setState({ loading: true })
        bulkDeleteReadingBooks(selectedIds)
            .then((res) => {
                store.dispatch(
                    showAlert({
                        message:
                            res?.message ||
                            `${selectedIds.length} record(s) deleted successfully`,
                        severity: ALERT.SUCCESS,
                        duration: 3000,
                    })
                )
                fetchData()
            })
            .catch((error) => {
                setState({ loading: false })
                store.dispatch(
                    showAlert({
                        message:
                            error?.response?.data?.message ||
                            'Failed to delete selected records',
                        severity: ALERT.ERROR,
                        duration: 5000,
                    })
                )
            })
    }

    return (
        <Root className="reading-books-container">
            <Paper className="p-3">
                <div className="table-header-container">
                    <div className="filter-header">
                        <TextField
                            id="input-with-icon-textfield"
                            label="Search"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <GridSearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            name="search"
                            onChange={(e) =>
                                onFilterChange(e.target.name, e.target.value)
                            }
                            autoComplete="off"
                            value={filters.search}
                            className={'search-field'}
                        />

                        <FormControl variant="outlined">
                            <InputLabel id="category-label">
                                Category
                            </InputLabel>
                            <Select
                                labelId="category-label"
                                id="category-dropdown"
                                value={filters.category}
                                onChange={(e) =>
                                    onFilterChange(
                                        e.target.name,
                                        e.target.value
                                    )
                                }
                                label="Category"
                                name="category"
                            >
                                <MenuItem value={''}>All Categories</MenuItem>
                                {BOOK_CATEGORY.getAll().map((cat) => (
                                    <MenuItem key={cat.id} value={cat.id}>
                                        {cat.text}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Button
                            onClick={() => setState({ modal: true })}
                            color="primary"
                            variant="contained"
                            size="large"
                        >
                            Add Book
                        </Button>
                        <Button
                            onClick={handleBulkDelete}
                            color="error"
                            variant="outlined"
                            size="large"
                            startIcon={<Delete />}
                            disabled={!selectedIds.length || loading}
                        >
                            Delete Selected
                        </Button>
                    </div>
                </div>
            </Paper>
            <Paper className="p-3 m-top-16">
                <div className="bulk-upload-container">
                    <h3>Bulk Upload</h3>
                    <div className="bulk-upload-actions">
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<CloudDownload />}
                            onClick={downloadTemplate}
                            size="large"
                        >
                            Download Template
                        </Button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,.xlsx,.xls"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                        />
                        <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<CloudUpload />}
                            onClick={() => fileInputRef.current?.click()}
                            size="large"
                        >
                            Select File
                        </Button>
                        {uploadFile && (
                            <span className="file-name">
                                {uploadFile.name}
                            </span>
                        )}
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleBulkUpload}
                            disabled={!uploadFile || loading}
                            size="large"
                        >
                            Upload
                        </Button>
                    </div>
                </div>
            </Paper>
            <div style={{ background: 'white' }}>
                <CustomDataGrid
                    rows={modifyProperty(data, '_id', 'id').map((el) => ({
                        ...el,
                        categoryId: el.category,
                        category: BOOK_CATEGORY.getById(el.category)?.text,
                    }))}
                    columns={headCells.map((col) => ({
                        ...col,
                        sortable: true,
                        disableClickEventBubbling: true,
                        ...(isMobile
                            ? rowWidthConfig[col.field].mobile ||
                            rowWidthConfig[col.field].default
                            : rowWidthConfig[col.field].default),
                    }))}
                    rowCount={filters.total_results}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: filters.pageSize,
                            },
                        },
                    }}
                    disableColumnFilter
                    loading={loading}
                    paginationModel={filters}
                    checkboxSelection
                    disableRowSelectionOnClick
                    rowSelectionModel={selectedIds}
                    onRowSelectionModelChange={handleSelectionChange}
                    onPaginationModelChange={(page) =>
                        onPageSizeChange({
                            pageSize: page.pageSize,
                            page:
                                page.pageSize != filters.pageSize
                                    ? 0
                                    : page.page,
                        })
                    }
                />
            </div>
            <Dialog
                open={!!modal}
                onClose={handleCloseModal}
                className={classes.modal}
            >
                <div style={styles.paper}>
                    <h1>{editMode ? 'Update Book' : 'Create Book'}</h1>
                    <FormGroup row={1} sx={{ width: '100%' }}>
                        <TextField
                            className="m-top-16"
                            label="Title"
                            variant="outlined"
                            value={form.title}
                            required
                            type="text"
                            name="title"
                            onChange={(e) =>
                                handleInput(e.target.name, e.target.value)
                            }
                            fullWidth
                        />
                        <TextField
                            className="m-top-16"
                            label="Author"
                            variant="outlined"
                            value={form.author}
                            required
                            type="text"
                            name="author"
                            onChange={(e) =>
                                handleInput(e.target.name, e.target.value)
                            }
                            fullWidth
                        />
                        <FormControl
                            variant="outlined"
                            fullWidth
                            className="m-top-16"
                            required
                        >
                            <InputLabel id="category-label">
                                Category
                            </InputLabel>
                            <Select
                                labelId="category-label"
                                id="category-select"
                                value={form.category}
                                onChange={(e) =>
                                    handleInput(e.target.name, e.target.value)
                                }
                                label="Category"
                                name="category"
                            >
                                <MenuItem value={''}>Select</MenuItem>
                                {BOOK_CATEGORY.getAll().map((cat) => (
                                    <MenuItem key={cat.id} value={cat.id}>
                                        {cat.text}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            className="m-top-16"
                            label="Pages"
                            variant="outlined"
                            value={form.pages}
                            required
                            type="number"
                            name="pages"
                            onChange={(e) =>
                                handleInput(e.target.name, e.target.value)
                            }
                            fullWidth
                        />
                        <TextField
                            className="m-top-16"
                            label="Duration (Days)"
                            variant="outlined"
                            value={form.duration_days}
                            required
                            type="number"
                            name="duration_days"
                            onChange={(e) =>
                                handleInput(e.target.name, e.target.value)
                            }
                            fullWidth
                        />
                        <TextField
                            className="m-top-16"
                            label="Description"
                            variant="outlined"
                            value={form.description}
                            type="text"
                            name="description"
                            onChange={(e) =>
                                handleInput(e.target.name, e.target.value)
                            }
                            multiline
                            rows={3}
                            fullWidth
                        />
                        <TextField
                            className="m-top-16"
                            label="URL"
                            variant="outlined"
                            value={form.url}
                            type="url"
                            name="url"
                            onChange={(e) =>
                                handleInput(e.target.name, e.target.value)
                            }
                            fullWidth
                        />
                    </FormGroup>
                    <div style={{ marginTop: '16px' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<CheckCircleRounded />}
                            onClick={handleSubmit}
                            aria-label="Submit"
                            style={{ marginRight: '12px' }}
                            disabled={loading}
                        >
                            {editMode ? 'Update' : 'Create'}
                        </Button>
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleCloseModal}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            </Dialog>
        </Root>
    )
}

export default ReadingBooksContainer

